import { representativesCarryingOf } from '@e-socna/folder-actors';

import type { ActorsCmp_ActorableRules } from '../fragments/actorable-rules.fragment';

interface RoleLabels {
  readonly baseRoleLabel: string;
  readonly roleLabelOf: (roleId: string) => string;
  readonly representativeTypeLabelOf: (typeId: string) => string;
}

// The list a rule applies to: the internal actors (the group doing the work) or the external ones
// (facing them, a recharged internal included).
export type RuleList = 'internal' | 'external';

interface Description {
  readonly list: RuleList;
  readonly text: string;
}

const describe = (
  isSet: boolean | null | undefined,
  list: RuleList,
  text: string,
): Description | undefined => (isSet ? { list, text } : undefined);

// The rules a workflow sets on the actors of a list, as the lines of the rules card.
export const actorableRuleDescriptionsOf = (
  rules: ActorsCmp_ActorableRules,
  list: RuleList,
  {
    baseRoleLabel,
    roleLabelOf,
    representativeTypeLabelOf,
  }: Readonly<RoleLabels>,
): readonly string[] =>
  [
    describe(
      rules.atLeastOneExternal,
      'external',
      'Au moins un acteur externe doit être renseigné',
    ),
    describe(
      rules.atLeastOneInternal,
      'internal',
      'Au moins un acteur interne doit être renseigné',
    ),
    describe(
      rules.atMostOneInternal,
      'internal',
      'Un seul acteur interne, choisi parmi ceux du niveau parent',
    ),
    describe(
      !!rules.requiredInternalContextualRoleIds.length,
      'internal',
      `Chaque organisation interne doit posséder ${representativesCarryingOf('role', rules.requiredInternalContextualRoleIds.map(roleLabelOf))}`,
    ),
    describe(
      rules.oneAndOnlyOneQuotationEmitter,
      'internal',
      'Un et un seul acteur doit être émetteur du devis',
    ),
    describe(
      !!rules.requiredQuotationEmitterRepresentativeTypeIds.length,
      'internal',
      `L'émetteur du devis doit posséder ${representativesCarryingOf('type', rules.requiredQuotationEmitterRepresentativeTypeIds.map(representativeTypeLabelOf))}`,
    ),
    describe(
      rules.oneAndOnlyOneQuotationReceiver,
      'external',
      'Un et un seul acteur doit être destinataire du devis',
    ),
    describe(
      rules.allowsInternalQuotationReceiver,
      'external',
      'Un acteur interne peut être destinataire du devis (refacturation interne)',
    ),
    describe(
      rules.atLeastOneRepresentativeForEachExternalOrganization,
      'external',
      'Chaque organisation externe doit avoir au moins un représentant',
    ),
    describe(
      rules.atLeastOneRepresentativeForQuotationReceiver,
      'external',
      'Le destinataire du devis (organisation) doit avoir au moins un représentant',
    ),
    describe(
      !!rules.requiredQuotationReceiverRepresentativeTypeIds.length,
      'external',
      `Le destinataire du devis (organisation) doit posséder ${representativesCarryingOf('type', rules.requiredQuotationReceiverRepresentativeTypeIds.map(representativeTypeLabelOf))}`,
    ),
    describe(
      rules.oneAndOnlyOneEdmDomain,
      'internal',
      'Un et un seul acteur interne doit porter le domaine GED',
    ),
    describe(
      rules.giveBaseRoleToAllInternalUsers,
      'internal',
      `Tous les utilisateurs internes reçoivent le rôle ${baseRoleLabel}`,
    ),
    describe(
      rules.giveBaseRoleToAllRelatedInternalOrganizationMembers,
      'internal',
      `Les membres des organisations internes reçoivent le rôle ${baseRoleLabel}`,
    ),
    describe(
      rules.giveBaseRoleToAllRelatedExternalOrganizationMembers,
      'external',
      `Les membres des organisations externes reçoivent le rôle ${baseRoleLabel}`,
    ),
  ]
    .filter(
      (description): description is Description => description?.list === list,
    )
    .map(({ text }) => text);
